/**
 * Manual intervention: operator settings (baseline lock, competitor mute, alt ladders, logit scale)
 * are independent policy — not “quote overrides”. Suspend/shaping live below.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useGateMarket,
  useUngateMarket,
  useMarketOverride,
  useSetMuteCompetitorSignals,
  useSetMuteBookCompetitor,
  useOrderBook,
  useSetManualBaselineLock,
  useClearManualBaselineLock,
  useInjectMarketUncertainty,
  useReseedMarketPosterior,
} from '../api/hooks';
import { useAuth } from '../auth/AuthContext';
import {
  parseProbMarginInput,
  previewFixedMarginImplied,
  quoteMarginationClamps,
} from '../utils/fixedMarginPreview';
import { useHomebaseModal } from './HomebaseModal';
import {
  logitProb,
  reseedConfirmBody,
  reseedErrorMessage,
  reseedInputsFromQuote,
  reseedPreview as buildReseedPreview,
  reseedSqrtPGateWarning,
  reseedValidationError,
  sigmoidLogit,
} from '../utils/reseedPosterior';
import './ManualGatingPanel.css';

const UNCERTAINTY_PRESETS = [0.01, 0.02, 0.04];

function ManualGatingPanel({ marketKey, quote }) {
  const [logitInput, setLogitInput] = useState('');
  const [probMarginOverInput, setProbMarginOverInput] = useState('');
  const [probMarginUnderInput, setProbMarginUnderInput] = useState('');
  const [baselineLockInput, setBaselineLockInput] = useState('');
  const [altAllowChecked, setAltAllowChecked] = useState(false);
  const [altFloorInput, setAltFloorInput] = useState('');
  const [altCeilInput, setAltCeilInput] = useState('');
  const [altFloorDirty, setAltFloorDirty] = useState(false);
  const [altCeilDirty, setAltCeilDirty] = useState(false);
  const [uncertaintyDeltaInput, setUncertaintyDeltaInput] = useState('0.02');
  const [reseedLineInput, setReseedLineInput] = useState('');
  const [reseedPInput, setReseedPInput] = useState('');
  const [reseedProbInput, setReseedProbInput] = useState('');
  const [reseedXInput, setReseedXInput] = useState('');
  const [reseedLockBaseline, setReseedLockBaseline] = useState(true);
  const [reseedPrefillKey, setReseedPrefillKey] = useState(null);
  const { user } = useAuth();

  const gatedBy = useMemo(() => user?.email?.trim() || 'manual', [user?.email]);

  const { data: override, isLoading: overrideLoading } = useMarketOverride(marketKey);
  const gateMarket = useGateMarket();
  const ungateMarket = useUngateMarket();
  const setManualBaselineLock = useSetManualBaselineLock();
  const clearManualBaselineLock = useClearManualBaselineLock();
  const setMuteCompetitor = useSetMuteCompetitorSignals();
  const setMuteBook = useSetMuteBookCompetitor();
  const injectUncertainty = useInjectMarketUncertainty();
  const reseedPosterior = useReseedMarketPosterior();
  const { data: orderBook } = useOrderBook(marketKey);
  const [bookMuteTarget, setBookMuteTarget] = useState('');
  const { confirm, alert, ModalLayer } = useHomebaseModal();

  const manualSuspend = override?.manual_suspend === true;
  const manualShapingActive = override?.manual_shaping_active === true;
  const isManuallyGated = override?.is_manually_gated === true;
  const hasManualRow = isManuallyGated || manualSuspend || manualShapingActive;
  const hasForceTwoSided = override?.force_two_sided === true;
  const hasForceOneSided = override?.force_one_sided === true;
  const hasManualLogit =
    override?.manual_logit_margin_scale != null &&
    !Number.isNaN(Number(override.manual_logit_margin_scale));
  const hasManualProbMarginOver =
    override?.manual_prob_margin_over != null &&
    !Number.isNaN(Number(override.manual_prob_margin_over));
  const hasManualProbMarginUnder =
    override?.manual_prob_margin_under != null &&
    !Number.isNaN(Number(override.manual_prob_margin_under));
  const hasManualProbMargin = hasManualProbMarginOver || hasManualProbMarginUnder;
  const isPending =
    gateMarket.isPending ||
    ungateMarket.isPending ||
    setMuteCompetitor.isPending ||
    setMuteBook.isPending ||
    setManualBaselineLock.isPending ||
    clearManualBaselineLock.isPending ||
    injectUncertainty.isPending ||
    reseedPosterior.isPending;
  const quoteP =
    quote?.P != null && Number.isFinite(Number(quote.P)) ? Number(quote.P) : null;
  const quoteSqrtP = quoteP != null && quoteP >= 0 ? Math.sqrt(quoteP) : null;
  const muteCompetitorOn = override?.mute_competitor_signals === true;
  const manualBaselineLocked = override?.manual_base_line != null && !Number.isNaN(Number(override.manual_base_line));
  const altOperatorExplicit = override?.manual_alts_allow != null;
  const altMarginsStored =
    override?.manual_alt_margin_floor != null || override?.manual_alt_margin_ceiling != null;
  const baselineLockedActive = manualBaselineLocked;
  const competitorMutedActive = muteCompetitorOn;
  const altPolicyActive = altOperatorExplicit || altMarginsStored;
  const altBadgeOnlyClamps = altMarginsStored && !altOperatorExplicit;
  const altHeaderBadgeLabel = altBadgeOnlyClamps ? 'ALT Δp' : altAllowChecked ? 'ALTS ON' : 'ALTS OFF';
  const logitCustomActive = hasManualLogit;
  const probMarginCustomActive = hasManualProbMargin;
  const quoteLineEff =
    quote?.line != null && Number.isFinite(Number(quote.line)) ? Number(quote.line) : null;

  const fmtQuoteDp = (v, decimals) => {
    if (v == null || !Number.isFinite(Number(v))) return '—';
    return Number(v).toFixed(decimals);
  };

  useEffect(() => {
    const L = quote?.line;
    if (manualBaselineLocked) return;
    if (L != null && Number.isFinite(Number(L))) {
      setBaselineLockInput(String(L));
    }
  }, [quote?.line, manualBaselineLocked]);

  useEffect(() => {
    if (!marketKey || !quote) return;
    if (reseedPrefillKey === marketKey) return;
    // Wait until we have at least fair or P so empty quotes don't lock the form empty.
    const seed = reseedInputsFromQuote(quote);
    if (!seed) return;
    setReseedLineInput(seed.line);
    setReseedProbInput(seed.prob);
    setReseedXInput(seed.x);
    setReseedPInput(seed.P);
    setReseedLockBaseline(true);
    setReseedPrefillKey(marketKey);
  }, [marketKey, quote, reseedPrefillKey]);

  useEffect(() => {
    if (overrideLoading) return;
    if (override?.manual_alts_allow === true) setAltAllowChecked(true);
    else setAltAllowChecked(false);
    setAltFloorInput(
      override?.manual_alt_margin_floor != null && Number.isFinite(Number(override.manual_alt_margin_floor))
        ? String(override.manual_alt_margin_floor)
        : ''
    );
    setAltCeilInput(
      override?.manual_alt_margin_ceiling != null && Number.isFinite(Number(override.manual_alt_margin_ceiling))
        ? String(override.manual_alt_margin_ceiling)
        : ''
    );
    setAltFloorDirty(false);
    setAltCeilDirty(false);
  }, [
    overrideLoading,
    override?.manual_alts_allow,
    override?.manual_alt_margin_floor,
    override?.manual_alt_margin_ceiling,
  ]);

  useEffect(() => {
    if (overrideLoading) return;
    if (
      override?.manual_logit_margin_scale != null &&
      Number.isFinite(Number(override.manual_logit_margin_scale))
    ) {
      setLogitInput(String(override.manual_logit_margin_scale));
    } else {
      setLogitInput('');
    }
  }, [overrideLoading, override?.manual_logit_margin_scale]);

  useEffect(() => {
    if (overrideLoading) return;
    setProbMarginOverInput(
      override?.manual_prob_margin_over != null && Number.isFinite(Number(override.manual_prob_margin_over))
        ? String(override.manual_prob_margin_over)
        : ''
    );
    setProbMarginUnderInput(
      override?.manual_prob_margin_under != null && Number.isFinite(Number(override.manual_prob_margin_under))
        ? String(override.manual_prob_margin_under)
        : ''
    );
  }, [overrideLoading, override?.manual_prob_margin_over, override?.manual_prob_margin_under]);

  const qn = (key) => {
    const v = quote?.[key];
    return v != null && Number.isFinite(Number(v)) ? Number(v) : null;
  };
  const effScale = qn('margination_logit_scale');
  const logitCiSpread = qn('margination_logit_ci_spread');
  const probMarginRawOver = qn('margination_raw_prob_margin_over');
  const probMarginRawUnder = qn('margination_raw_prob_margin_under');
  const probMarginStructOver = qn('margination_structural_prob_margin_over');
  const probMarginStructUnder = qn('margination_structural_prob_margin_under');
  const probMarginEffOver = qn('margination_effective_prob_margin_over');
  const probMarginEffUnder = qn('margination_effective_prob_margin_under');
  const tobMargin = quote?.gate_breakdown?.soft_engine?.tob_margin || {};
  const tobCapping = tobMargin.active_cap === true;
  const tobOver = tobMargin.tob_over != null && Number.isFinite(Number(tobMargin.tob_over))
    ? Number(tobMargin.tob_over)
    : null;
  const tobUnder = tobMargin.tob_under != null && Number.isFinite(Number(tobMargin.tob_under))
    ? Number(tobMargin.tob_under)
    : null;
  const fmtMargin = (v) => (v != null ? v.toFixed(4) : '—');
  const ciWidthProb = qn('ci_width');
  const logitInputValue = useMemo(() => {
    const parsed = Number.parseFloat(String(logitInput).trim());
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
  }, [logitInput]);

  const liveLogitPreview = useMemo(() => {
    if (logitInputValue == null) return null;
    const qnLocal = (key) => {
      const v = quote?.[key];
      return v != null && Number.isFinite(Number(v)) ? Number(v) : null;
    };
    const pFair = qnLocal('p_fair');
    const pUpper = qnLocal('p_upper');
    const pLower = qnLocal('p_lower');
    const floor = qnLocal('margination_floor');
    const ceiling = qnLocal('margination_ceiling');
    if (pFair == null || pUpper == null || pLower == null || floor == null || ceiling == null) return null;

    const clampProb = (v) => Math.max(1e-6, Math.min(1 - 1e-6, v));
    const sigmoid = (x) => 1 / (1 + Math.exp(-x));
    const toLogit = (p) => Math.log(p / (1 - p));
    const previewSide = (pFairSide, pUpperSide) => {
      const fair = clampProb(pFairSide);
      const hi = clampProb(pUpperSide);
      const logitFair = toLogit(fair);
      const logitHi = toLogit(hi);
      const shift = (logitHi - logitFair) * logitInputValue;
      const rawProb = sigmoid(logitFair + shift);
      const clampedProb = Math.max(fair + floor, Math.min(fair + ceiling, rawProb));
      return {
        impliedProb: clampProb(clampedProb),
      };
    };

    const over = previewSide(pFair, pUpper);
    const fallbackFairUnder = pFair != null ? 1 - pFair : null;
    const fallbackLowerUnder = pUpper != null ? 1 - pUpper : null;
    const fallbackUpperUnder = pLower != null ? 1 - pLower : null;
    const pFairUnder = qnLocal('p_fair_under') ?? fallbackFairUnder;
    const pUpperUnder = qnLocal('p_upper_under') ?? fallbackUpperUnder;
    const pLowerUnder = qnLocal('p_lower_under') ?? fallbackLowerUnder;
    const canPriceUnder =
      Boolean(quote?.quote_two_sided) &&
      pFairUnder != null &&
      pUpperUnder != null &&
      pLowerUnder != null;
    const under = canPriceUnder ? previewSide(pFairUnder, pUpperUnder) : null;

    return {
      scale: logitInputValue,
      over,
      under,
      canPriceUnder,
    };
  }, [logitInputValue, quote]);

  const liveFixedMarginPreview = useMemo(() => {
    const overStr = String(probMarginOverInput).trim();
    const underStr = String(probMarginUnderInput).trim();
    if (!overStr && !underStr) return null;

    const qnLocal = (key) => {
      const v = quote?.[key];
      return v != null && Number.isFinite(Number(v)) ? Number(v) : null;
    };
    const { floor, ceiling } = quoteMarginationClamps(quote);
    const pFair = qnLocal('p_fair');
    const pFairUnder = qnLocal('p_fair_under') ?? (pFair != null ? 1 - pFair : null);
    const line = quoteLineEff;

    const previewSide = (rawStr, pFairSide) => {
      if (!rawStr || pFairSide == null) return null;
      const m = Number.parseFloat(rawStr);
      if (!Number.isFinite(m) || m < 0) return null;
      const implied = previewFixedMarginImplied(pFairSide, m, floor, ceiling);
      if (implied == null) return null;
      const pc = Math.max(1e-6, Math.min(1 - 1e-6, pFairSide));
      return {
        requested: m,
        implied,
        decimal: 1 / implied,
        effDelta: implied - pc,
      };
    };

    const over = previewSide(overStr, pFair);
    const canPriceUnder = Boolean(quote?.quote_two_sided) && pFairUnder != null;
    const under = canPriceUnder ? previewSide(underStr, pFairUnder) : null;
    const postedOver = qnLocal('marginated_price') ?? qnLocal('marginated_price_if_gated');
    const postedUnder = qnLocal('marginated_price_under') ?? qnLocal('marginated_price_under_if_gated');

    return {
      line,
      over,
      under,
      canPriceUnder,
      postedOver,
      postedUnder,
      floor,
      ceiling,
    };
  }, [probMarginOverInput, probMarginUnderInput, quote, quoteLineEff]);

  const runGate = useCallback(
    async (payload) => {
      await gateMarket.mutateAsync({
        marketKey,
        gatedBy,
        ...payload,
      });
      // Gate mutation refetches quote + override (see useGateMarket); logit input syncs from override effect.
    },
    [gateMarket, marketKey, gatedBy]
  );

  const handleSuspend = async () => {
    const ok = await confirm({
      title: 'Suspend quoting',
      body: 'Suspend quoting for this market? Published quotes will block until you remove the override.',
      danger: true,
      confirmLabel: 'Suspend',
    });
    if (!ok) return;
    try {
      await runGate({});
    } catch (err) {
      console.error('Failed to suspend:', err);
    }
  };

  const handleTwoSided = async () => {
    const ok = await confirm({
      title: 'Two-sided shaping',
      body:
        'Apply two-sided quote shaping and refresh OB depth (two-sided ladder)? NO-side synthetic depth stays when the quote is two-sided.',
      confirmLabel: 'Apply',
    });
    if (!ok) return;
    try {
      await runGate({ force_two_sided: true });
    } catch (err) {
      console.error('Failed to apply two-sided:', err);
    }
  };

  const handleOneSided = async () => {
    const ok = await confirm({
      title: 'One-sided shaping',
      body:
        'Apply one-sided quote shaping and refresh OB as over-only? Synthetic NO depth will be stripped after re-seed.',
      confirmLabel: 'Apply',
    });
    if (!ok) return;
    try {
      await runGate({ force_one_sided: true });
    } catch (err) {
      console.error('Failed to apply one-sided:', err);
    }
  };

  const handleLogitCardApply = async () => {
    const v = parseFloat(String(logitInput).trim());
    if (!Number.isFinite(v) || v < 0) {
      await alert({
        title: 'Logit scale',
        body: 'Enter a non-negative number.',
        warn: true,
      });
      return;
    }
    const ok = await confirm({
      title: 'Logit scale',
      body: `Set operator logit scale to ${v.toFixed(3)}? OB depth refreshes on the writer.`,
      confirmLabel: 'Apply',
    });
    if (!ok) return;
    try {
      await runGate({ manual_logit_margin_scale: v });
    } catch (err) {
      console.error('Failed to set logit scale:', err);
    }
  };

  const handleLogitCardClear = async () => {
    const ok = await confirm({
      title: 'Clear logit scale',
      body: 'Remove operator logit margination scale?',
      confirmLabel: 'Clear',
    });
    if (!ok) return;
    try {
      await runGate({ manual_logit_margin_scale: null });
    } catch (err) {
      console.error('Failed to clear logit scale:', err);
    }
  };

  const handleProbMarginApply = async () => {
    let overVal;
    let underVal;
    try {
      overVal = parseProbMarginInput(probMarginOverInput, 'Over margin');
      underVal = parseProbMarginInput(probMarginUnderInput, 'Under margin');
    } catch (err) {
      await alert({
        title: 'Fixed margin',
        body: err.message || 'Invalid margin value.',
        warn: true,
      });
      return;
    }
    if (overVal == null && underVal == null) {
      await alert({
        title: 'Fixed margin',
        body: 'Enter at least one over or under margin (Δp), or use Clear.',
        warn: true,
      });
      return;
    }
    const ok = await confirm({
      title: 'Fixed margin',
      body: `Set operator fixed margin${overVal != null ? ` over=${overVal.toFixed(3)}` : ''}${underVal != null ? ` under=${underVal.toFixed(3)}` : ''}?`,
      confirmLabel: 'Apply',
    });
    if (!ok) return;
    try {
      const payload = {};
      if (String(probMarginOverInput).trim() !== '') payload.manual_prob_margin_over = overVal;
      else payload.manual_prob_margin_over = null;
      if (String(probMarginUnderInput).trim() !== '') payload.manual_prob_margin_under = underVal;
      else payload.manual_prob_margin_under = null;
      await runGate(payload);
    } catch (err) {
      console.error('Failed to set fixed margin:', err);
    }
  };

  const handleProbMarginClear = async () => {
    const ok = await confirm({
      title: 'Clear fixed margin',
      body: 'Remove operator fixed over/under probability margins?',
      confirmLabel: 'Clear',
    });
    if (!ok) return;
    try {
      await runGate({ manual_prob_margin_over: null, manual_prob_margin_under: null });
    } catch (err) {
      console.error('Failed to clear fixed margin:', err);
    }
  };

  const handleApplyAltLadders = async () => {
    if ((altFloorDirty || altCeilDirty) && !altAllowChecked) {
      await alert({
        title: 'Alt ladders',
        body: 'Turn on “Serve alts” before editing marg. floor / marg. ceiling.',
        warn: true,
      });
      return;
    }
    const fs = String(altFloorInput).trim();
    const cs = String(altCeilInput).trim();
    let manual_alt_margin_floor;
    let manual_alt_margin_ceiling;
    if (altFloorDirty) {
      if (fs === '') {
        manual_alt_margin_floor = null;
      } else {
        const v = parseFloat(fs);
        if (!Number.isFinite(v) || v < 0 || v > 0.5) {
          await alert({
            title: 'Invalid alt margin floor',
            body: 'Use a number between 0 and 0.5 (probability vs fair), or leave blank to clear the stored floor.',
            warn: true,
          });
          return;
        }
        manual_alt_margin_floor = v;
      }
    }
    if (altCeilDirty) {
      if (cs === '') {
        manual_alt_margin_ceiling = null;
      } else {
        const v = parseFloat(cs);
        if (!Number.isFinite(v) || v < 0 || v > 0.5) {
          await alert({
            title: 'Invalid alt margin ceiling',
            body: 'Use a number between 0 and 0.5 (probability vs fair), or leave blank to clear the stored ceiling.',
            warn: true,
          });
          return;
        }
        manual_alt_margin_ceiling = v;
      }
    }
    const ok = await confirm({
      title: 'Alt ladders',
      body: `${altAllowChecked ? 'Serve' : 'Hold back'} alt ladders for this market? ${altFloorDirty || altCeilDirty ? 'Updates stored alt marg. floor / marg. ceiling.' : ''}`.trim(),
      confirmLabel: 'Apply',
    });
    if (!ok) return;
    try {
      const payload = { manual_alts_allow: altAllowChecked };
      if (altAllowChecked && altFloorDirty) payload.manual_alt_margin_floor = manual_alt_margin_floor;
      if (altAllowChecked && altCeilDirty) payload.manual_alt_margin_ceiling = manual_alt_margin_ceiling;
      await runGate(payload);
      setAltFloorDirty(false);
      setAltCeilDirty(false);
    } catch (err) {
      console.error('Failed to apply alt ladder policy:', err);
    }
  };

  const handleClearManualAlt = async () => {
    if (!altPolicyActive) return;
    const ok = await confirm({
      title: 'Clear manual alt settings',
      body: 'Remove stored manual serve/clamps? Quoted alt Δp then follows engine defaults (compare quote alt vs main Δp below).',
      danger: false,
      confirmLabel: 'Clear',
    });
    if (!ok) return;
    try {
      await runGate({
        manual_alts_allow: null,
        manual_alt_margin_floor: null,
        manual_alt_margin_ceiling: null,
      });
      setAltFloorDirty(false);
      setAltCeilDirty(false);
    } catch (err) {
      console.error('Failed to clear manual alt settings:', err);
      await alert({
        title: 'Request failed',
        body: err?.response?.data?.detail || err?.message || 'Could not clear manual alt settings.',
        warn: true,
      });
    }
  };

  const handleMuteCompetitorToggle = async () => {
    const next = !muteCompetitorOn;
    const ok = await confirm({
      title: next ? 'Mute competitor' : 'Resume competitor',
      body: next
        ? 'Stop base-KF → EntriesKF z² sync and z²-gated synthetic OB ladder refresh.'
        : 'Restore base-KF competitor sync and z²-gated OB refresh.',
      danger: false,
      confirmLabel: next ? 'Mute' : 'Resume',
    });
    if (!ok) return;
    try {
      await setMuteCompetitor.mutateAsync({ marketKey, enabled: next });
    } catch (err) {
      console.error('Failed to set competitor mute:', err);
      await alert({
        title: 'Request failed',
        body: err?.response?.data?.detail || err?.message || 'Could not update mute setting.',
        warn: true,
      });
    }
  };

  const competitorBooks = useMemo(() => {
    const rows = orderBook?.competitor_books || [];
    return rows.map((b) => ({
      name: b.book || b.sportsbook || 'Book',
      muted: Boolean(b.competitor_muted),
    }));
  }, [orderBook?.competitor_books]);

  useEffect(() => {
    if (bookMuteTarget) return;
    if (competitorBooks.length) setBookMuteTarget(competitorBooks[0].name);
  }, [bookMuteTarget, competitorBooks]);

  const selectedBookMuted = competitorBooks.find((b) => b.name === bookMuteTarget)?.muted;

  const handleBookMuteToggle = async () => {
    if (!bookMuteTarget) return;
    const next = !selectedBookMuted;
    try {
      await setMuteBook.mutateAsync({
        marketKey,
        sportsbook: bookMuteTarget,
        enabled: next,
        reason: next ? 'manual_intervention' : null,
        muted_by: gatedBy,
      });
    } catch (err) {
      console.error('Failed to set book competitor mute:', err);
      await alert({
        title: 'Request failed',
        body: err?.response?.data?.detail || err?.message || 'Could not update book mute.',
        warn: true,
      });
    }
  };

  const handleUngate = async () => {
    const ok = await confirm({
      title: 'Clear quote gate',
      body:
        'Removes suspend, shaping, and manual prices. Operator settings stored on the same gate row (baseline lock, competitor mute, alt ladder policy, logit scale) clear together when the server removes that record.',
      danger: true,
      confirmLabel: 'Remove',
    });
    if (!ok) return;
    try {
      await ungateMarket.mutateAsync(marketKey);
    } catch (err) {
      console.error('Failed to ungate market:', err);
    }
  };

  const handleLockBaseline = async () => {
    const v = parseFloat(String(baselineLockInput).trim());
    if (!Number.isFinite(v)) {
      await alert({
        title: 'Invalid line',
        body: 'Enter a numeric baseline.',
        warn: true,
      });
      return;
    }
    const ok = await confirm({
      title: 'Lock KF baseline',
      body: `Fix baseline to ${v}. Auto line pick / baseline_shift off until cleared.`,
      danger: false,
      confirmLabel: 'Lock',
    });
    if (!ok) return;
    try {
      await setManualBaselineLock.mutateAsync({ marketKey, baseLine: v, setBy: gatedBy });
    } catch (err) {
      console.error('Failed to lock baseline:', err);
      await alert({
        title: 'Request failed',
        body: err?.response?.data?.detail || err?.message || 'Could not set baseline lock.',
        warn: true,
      });
    }
  };

  const handleUnlockBaseline = async () => {
    const ok = await confirm({
      title: 'Clear baseline lock',
      body: 'Resume auto line selection and baseline_shift.',
      danger: false,
      confirmLabel: 'Clear',
    });
    if (!ok) return;
    try {
      await clearManualBaselineLock.mutateAsync(marketKey);
    } catch (err) {
      console.error('Failed to clear baseline lock:', err);
      await alert({
        title: 'Request failed',
        body: err?.response?.data?.detail || err?.message || 'Could not clear baseline lock.',
        warn: true,
      });
    }
  };

  const handleInjectUncertainty = async (deltaOverride) => {
    const raw =
      deltaOverride != null ? deltaOverride : Number.parseFloat(String(uncertaintyDeltaInput).trim());
    if (!Number.isFinite(raw) || raw <= 0 || raw > 1) {
      await alert({
        title: 'Inject uncertainty',
        body: 'Enter a ΔP between 0 and 1 (logit variance, e.g. 0.02).',
        warn: true,
      });
      return;
    }
    const p0 = quoteP;
    const p1 = p0 != null ? p0 + raw : null;
    const sqrt1 = p1 != null && p1 >= 0 ? Math.sqrt(p1) : null;
    const ok = await confirm({
      title: 'Inject uncertainty',
      body:
        `Add ΔP = ${raw.toFixed(3)} to EntriesKF variance (fair unchanged).` +
        (p0 != null && p1 != null
          ? ` P ${p0.toFixed(4)} → ~${p1.toFixed(4)}${
              sqrt1 != null ? ` (√P ~${sqrt1.toFixed(3)})` : ''
            }.`
          : '') +
        ' Untagged (does not decay with jump half-life). May fail the √P quote gate and widen OB clear moves.',
      danger: true,
      confirmLabel: 'Inject',
    });
    if (!ok) return;
    try {
      const res = await injectUncertainty.mutateAsync({
        marketKey,
        deltaP: raw,
        actedBy: gatedBy,
      });
      if (res?.would_fail_sqrt_p_gate) {
        await alert({
          title: 'Uncertainty injected',
          body: `P ${Number(res.P_before).toFixed(4)} → ${Number(res.P_after).toFixed(4)} (√P ${Number(
            res.sqrt_P_after
          ).toFixed(3)} > max ${Number(res.max_sqrt_p).toFixed(3)}). Quote may gate on √P until P decays.`,
          warn: true,
        });
      }
    } catch (err) {
      console.error('Failed to inject uncertainty:', err);
      const detail = err?.response?.data?.detail;
      const msg =
        (typeof detail === 'object' && detail?.message) ||
        (typeof detail === 'string' ? detail : null) ||
        err?.message ||
        'Could not inject uncertainty.';
      await alert({ title: 'Request failed', body: msg, warn: true });
    }
  };

  const reseedPreview = useMemo(
    () =>
      buildReseedPreview({
        line: reseedLineInput,
        P: reseedPInput,
        prob: reseedProbInput,
        x: reseedXInput,
      }),
    [reseedLineInput, reseedPInput, reseedProbInput, reseedXInput]
  );

  const handleReseedProbChange = (val) => {
    setReseedProbInput(val);
    const p = Number.parseFloat(String(val).trim());
    if (Number.isFinite(p) && p > 0 && p < 1) {
      setReseedXInput(logitProb(p).toFixed(4));
    }
  };

  const handleReseedXChange = (val) => {
    setReseedXInput(val);
    const x = Number.parseFloat(String(val).trim());
    if (Number.isFinite(x)) {
      setReseedProbInput(sigmoidLogit(x).toFixed(4));
    }
  };

  const handleLoadReseedFromQuote = () => {
    setReseedPrefillKey(null);
  };

  const handleReseedPosterior = async () => {
    const invalid = reseedValidationError(reseedPreview);
    if (invalid) {
      await alert({ title: 'Reseed', body: invalid, warn: true });
      return;
    }
    const { line, P, x } = reseedPreview;
    const ok = await confirm({
      title: 'Reseed posterior',
      body: reseedConfirmBody(reseedPreview, { lockBaseline: reseedLockBaseline }),
      danger: true,
      confirmLabel: 'Reseed',
    });
    if (!ok) return;
    try {
      const res = await reseedPosterior.mutateAsync({
        marketKey,
        line,
        P,
        x,
        lockBaseline: reseedLockBaseline,
        actedBy: gatedBy,
      });
      setReseedPrefillKey(null);
      const sqrtPWarning = reseedSqrtPGateWarning(res);
      if (sqrtPWarning) {
        await alert({ title: 'Reseeded (√P gate)', body: sqrtPWarning, warn: true });
      }
    } catch (err) {
      console.error('Failed to reseed posterior:', err);
      await alert({ title: 'Request failed', body: reseedErrorMessage(err), warn: true });
    }
  };

  if (overrideLoading) {
    return <div className="manual-intervention loading">Loading operator settings…</div>;
  }

  return (
    <div className="manual-intervention">
      <div className="manual-intervention-header">
        <span className="manual-intervention-title">Manual intervention</span>
        {manualSuspend && (
          <span className="manual-badge manual-badge-gated" title="Book suspended — quotes blocked">
            SUSPENDED
          </span>
        )}
        {manualShapingActive && !manualSuspend && (
          <span className="manual-badge manual-badge-open" title="Leave-open shaping — engine gate still applies">
            SHAPING
          </span>
        )}
        {baselineLockedActive && (
          <span className="manual-badge manual-badge-ind manual-badge-ind-baseline" title="KF baseline operator-locked">
            BASE LOCKED
          </span>
        )}
        {competitorMutedActive && (
          <span className="manual-badge manual-badge-ind manual-badge-ind-mute" title="Competitor (base-KF) signals muted">
            COMP MUTED
          </span>
        )}
        {altPolicyActive && (
          <span
            className={`manual-badge manual-badge-ind manual-badge-ind-alt${
              altBadgeOnlyClamps ? ' manual-badge-ind-alt-clamps' : altAllowChecked ? '' : ' manual-badge-ind-alt-block'
            }`}
            title={
              altBadgeOnlyClamps
                ? 'Operator Δp clamps on alt ladder margination'
                : altAllowChecked
                  ? 'Serving alt ladders when config allows; optional Δp clamps'
                  : 'Alt ladders held back for this market'
            }
          >
            {altHeaderBadgeLabel}
          </span>
        )}
        {logitCustomActive && (
          <span
            className="manual-badge manual-badge-ind manual-badge-ind-logit"
            title="Operator logit margination scale (independent of suspend/shaping)"
          >
            LOGIT
          </span>
        )}
      </div>

      {user?.email ? (
        <div className="manual-intervention-row manual-intervention-operator">
          <span className="manual-intervention-label">Operator</span>
          <span
            className="manual-intervention-email"
            title="Sent as gated_by on suspend/shaping and operator actions that accept it"
          >
            {user.email}
          </span>
        </div>
      ) : null}

      <p className="manual-ind-policy-note">Independent of quote suspend &amp; shaping — policy only.</p>

      <div className="manual-ind-summary-bar" role="status" aria-label="Operator settings status">
        <div className={`manual-ind-sum-item${baselineLockedActive ? ' manual-ind-sum-item--active' : ''}`}>
          <span className="manual-ind-sum-label">Baseline</span>
          <span className="manual-ind-sum-value">
            {baselineLockedActive ? (
              <>
                <strong>{Number(override.manual_base_line)}</strong>
                <span className="manual-ind-sum-tag">locked</span>
              </>
            ) : (
              <>
                Auto
                {quoteLineEff != null ? (
                  <span className="manual-ind-sum-hint"> · engine {quoteLineEff}</span>
                ) : null}
              </>
            )}
          </span>
        </div>
        <span className="manual-ind-sum-sep" aria-hidden>
          ·
        </span>
        <div className={`manual-ind-sum-item${competitorMutedActive ? ' manual-ind-sum-item--active' : ''}`}>
          <span className="manual-ind-sum-label">Competitor</span>
          <span className="manual-ind-sum-value">
            {competitorMutedActive ? (
              <>
                <strong>Muted</strong>
                <span className="manual-ind-sum-tag">policy</span>
              </>
            ) : (
              <>Live</>
            )}
          </span>
        </div>
        <span className="manual-ind-sum-sep" aria-hidden>
          ·
        </span>
        <div className={`manual-ind-sum-item${altPolicyActive ? ' manual-ind-sum-item--active' : ''}`}>
          <span className="manual-ind-sum-label">Alt ladders</span>
          <span className="manual-ind-sum-value">
            {!altOperatorExplicit && !altMarginsStored ? (
              <>Default</>
            ) : altBadgeOnlyClamps ? (
              <>
                <strong>Δp clamps</strong>
                <span className="manual-ind-sum-hint">
                  {' '}
                  {override?.manual_alt_margin_floor != null ? Number(override.manual_alt_margin_floor).toFixed(2) : '—'}/
                  {override?.manual_alt_margin_ceiling != null ? Number(override.manual_alt_margin_ceiling).toFixed(2) : '—'}
                </span>
                <span className="manual-ind-sum-tag">saved</span>
              </>
            ) : altAllowChecked ? (
              <>
                <strong>Serving</strong>
                {altMarginsStored ? (
                  <span className="manual-ind-sum-hint">
                    {' '}
                    Δp{' '}
                    {override?.manual_alt_margin_floor != null ? Number(override.manual_alt_margin_floor).toFixed(2) : '—'}/
                    {override?.manual_alt_margin_ceiling != null ? Number(override.manual_alt_margin_ceiling).toFixed(2) : '—'}
                  </span>
                ) : null}
                <span className="manual-ind-sum-tag">on</span>
              </>
            ) : (
              <>
                <strong>Held back</strong>
                <span className="manual-ind-sum-tag">off</span>
              </>
            )}
          </span>
        </div>
        <span className="manual-ind-sum-sep" aria-hidden>
          ·
        </span>
        <div className={`manual-ind-sum-item${probMarginCustomActive ? ' manual-ind-sum-item--active' : ''}`}>
          <span className="manual-ind-sum-label">Fixed Δp</span>
          <span className="manual-ind-sum-value">
            {probMarginCustomActive ? (
              <>
                <strong>
                  {hasManualProbMarginOver ? Number(override.manual_prob_margin_over).toFixed(3) : '—'}/
                  {hasManualProbMarginUnder ? Number(override.manual_prob_margin_under).toFixed(3) : '—'}
                </strong>
                <span className="manual-ind-sum-tag">custom</span>
              </>
            ) : (
              <>Auto</>
            )}
          </span>
        </div>
        <span className="manual-ind-sum-sep" aria-hidden>
          ·
        </span>
        <div className={`manual-ind-sum-item${logitCustomActive ? ' manual-ind-sum-item--active' : ''}`}>
          <span className="manual-ind-sum-label">Logit</span>
          <span className="manual-ind-sum-value">
            {logitCustomActive ? (
              <>
                <strong>{Number(override.manual_logit_margin_scale).toFixed(3)}</strong>
                <span className="manual-ind-sum-tag">custom</span>
              </>
            ) : (
              <>
                Auto
                {effScale != null ? (
                  <span className="manual-ind-sum-hint"> · eff {effScale.toFixed(3)}</span>
                ) : null}
              </>
            )}
          </span>
        </div>
      </div>

      <div className="manual-ind-deck">
        <div className={`manual-ind-card${baselineLockedActive ? ' manual-ind-card--active' : ''}`}>
          <div className="manual-ind-card-head">
            <span className="manual-ind-card-title">KF baseline</span>
            <span className={baselineLockedActive ? 'manual-ind-pill manual-ind-pill--on' : 'manual-ind-pill'}>
              {baselineLockedActive ? 'Locked' : 'Auto'}
            </span>
          </div>
          <div className="manual-ind-card-body">
            {manualBaselineLocked ? (
              <>
                <div className="manual-ind-card-status">
                  Locked at <strong>{Number(override.manual_base_line)}</strong>
                  {override.manual_base_line_set_by ? (
                    <span className="manual-ind-sum-hint"> · {override.manual_base_line_set_by}</span>
                  ) : null}
                </div>
                <button type="button" className="button" disabled={isPending} onClick={handleUnlockBaseline}>
                  Clear lock
                </button>
              </>
            ) : (
              <>
                <input
                  type="text"
                  className="manual-intervention-input manual-ind-card-input"
                  value={baselineLockInput}
                  onChange={(e) => setBaselineLockInput(e.target.value)}
                  aria-label="Baseline line to lock"
                />
                <button type="button" className="button" disabled={isPending} onClick={handleLockBaseline}>
                  Lock
                </button>
              </>
            )}
          </div>
        </div>

        <div className={`manual-ind-card${competitorMutedActive ? ' manual-ind-card--active' : ''}`}>
          <div className="manual-ind-card-head">
            <span className="manual-ind-card-title">Competitor signals</span>
            <span className={competitorMutedActive ? 'manual-ind-pill manual-ind-pill--on' : 'manual-ind-pill'}>
              {competitorMutedActive ? 'Muted' : 'Live'}
            </span>
          </div>
          <div className="manual-ind-card-body manual-ind-card-body--row">
            <button
              type="button"
              className={
                muteCompetitorOn ? 'manual-btn manual-btn-primary' : 'manual-btn manual-btn-secondary'
              }
              onClick={handleMuteCompetitorToggle}
              disabled={isPending}
            >
              {muteCompetitorOn ? 'Resume' : 'Mute'}
            </button>
            <span className="manual-ind-card-hint">
              {competitorMutedActive ? 'Base-KF z² sync & z² OB refresh off.' : 'Normal base-KF → EntriesKF.'}
            </span>
          </div>
        </div>

        <div className={`manual-ind-card${selectedBookMuted ? ' manual-ind-card--active' : ''}`}>
          <div className="manual-ind-card-head">
            <span className="manual-ind-card-title">Book competitor mute</span>
            <span className={selectedBookMuted ? 'manual-ind-pill manual-ind-pill--on' : 'manual-ind-pill'}>
              {selectedBookMuted ? 'Muted' : 'Live'}
            </span>
          </div>
          <div className="manual-ind-card-body manual-ind-card-body--row">
            <select
              className="manual-intervention-input manual-ind-card-input"
              value={bookMuteTarget}
              onChange={(e) => setBookMuteTarget(e.target.value)}
              disabled={!competitorBooks.length || isPending}
              aria-label="Sportsbook to mute"
            >
              {competitorBooks.length ? (
                competitorBooks.map((b) => (
                  <option key={b.name} value={b.name}>
                    {b.name}{b.muted ? ' (muted)' : ''}
                  </option>
                ))
              ) : (
                <option value="">No books</option>
              )}
            </select>
            <button
              type="button"
              className={
                selectedBookMuted ? 'manual-btn manual-btn-primary' : 'manual-btn manual-btn-secondary'
              }
              onClick={handleBookMuteToggle}
              disabled={isPending || !bookMuteTarget || !competitorBooks.length}
            >
              {selectedBookMuted ? 'Unmute book' : 'Mute book'}
            </button>
          </div>
          <div className="manual-ind-card-hint">
            Per-book competitor signals only; prices still stored and visible (greyed on Signals tab).
          </div>
        </div>

        <div className="manual-ind-card">
          <div className="manual-ind-card-head">
            <span className="manual-ind-card-title">Inject uncertainty</span>
            <span className="manual-ind-pill">
              {quoteP != null
                ? `P ${quoteP.toFixed(4)}${quoteSqrtP != null ? ` · √${quoteSqrtP.toFixed(3)}` : ''}`
                : 'P —'}
            </span>
          </div>
          <div className="manual-ind-card-body manual-ind-card-body--stack">
            <div className="manual-ind-logit-row">
              {UNCERTAINTY_PRESETS.map((d) => (
                <button
                  key={d}
                  type="button"
                  className="manual-btn manual-btn-secondary"
                  disabled={isPending}
                  onClick={() => handleInjectUncertainty(d)}
                  title={`Add ΔP = ${d} to EntriesKF`}
                >
                  +{d}
                </button>
              ))}
              <input
                type="text"
                inputMode="decimal"
                className="manual-intervention-input manual-ind-card-input"
                style={{ maxWidth: '4.5rem' }}
                value={uncertaintyDeltaInput}
                onChange={(e) => setUncertaintyDeltaInput(e.target.value)}
                aria-label="Custom uncertainty delta P"
                title="Custom ΔP (logit variance)"
              />
              <button
                type="button"
                className="manual-btn manual-btn-primary"
                disabled={isPending}
                onClick={() => handleInjectUncertainty()}
              >
                {injectUncertainty.isPending ? 'Injecting…' : 'Inject'}
              </button>
            </div>
            <span className="manual-ind-card-hint">
              EntriesKF only: P += ΔP, fair unchanged. Untagged (no jump decay). Widens CI / clear moves; may √P-gate.
            </span>
          </div>
        </div>

        <div className="manual-ind-card">
          <div className="manual-ind-card-head">
            <span className="manual-ind-card-title">Reseed posterior</span>
            <span className="manual-ind-pill">line · fair · P</span>
          </div>
          <div className="manual-ind-card-body manual-ind-card-body--stack">
            <div className="manual-ind-logit-row">
              <label className="manual-ind-alt-inline">
                <span className="manual-ind-alt-lbl">line</span>
                <input
                  type="text"
                  inputMode="decimal"
                  className="manual-intervention-input manual-ind-alt-input manual-ind-alt-input--narrow"
                  value={reseedLineInput}
                  onChange={(e) => setReseedLineInput(e.target.value)}
                  aria-label="Reseed baseline line"
                />
              </label>
              <label className="manual-ind-alt-inline">
                <span className="manual-ind-alt-lbl">p fair</span>
                <input
                  type="text"
                  inputMode="decimal"
                  className="manual-intervention-input manual-ind-alt-input manual-ind-alt-input--narrow"
                  value={reseedProbInput}
                  onChange={(e) => handleReseedProbChange(e.target.value)}
                  aria-label="Reseed fair probability"
                  title="Fair in probability space (0–1)"
                />
              </label>
              <label className="manual-ind-alt-inline">
                <span className="manual-ind-alt-lbl">x logit</span>
                <input
                  type="text"
                  inputMode="decimal"
                  className="manual-intervention-input manual-ind-alt-input manual-ind-alt-input--narrow"
                  value={reseedXInput}
                  onChange={(e) => handleReseedXChange(e.target.value)}
                  aria-label="Reseed fair logit"
                  title="Fair in logit space"
                />
              </label>
              <label className="manual-ind-alt-inline">
                <span className="manual-ind-alt-lbl">P</span>
                <input
                  type="text"
                  inputMode="decimal"
                  className="manual-intervention-input manual-ind-alt-input manual-ind-alt-input--narrow"
                  value={reseedPInput}
                  onChange={(e) => setReseedPInput(e.target.value)}
                  aria-label="Reseed posterior variance P"
                  title="Logit variance P"
                />
              </label>
            </div>
            <div className="manual-logit-preview manual-ind-logit-preview">
              {reseedPreview.p != null ? (
                <>
                  <span>
                    Preview fair <strong>{(reseedPreview.p * 100).toFixed(1)}%</strong>
                    {reseedPreview.x != null ? (
                      <span className="manual-intervention-hint">
                        {' '}
                        · x {reseedPreview.x.toFixed(4)}
                      </span>
                    ) : null}
                  </span>
                  {reseedPreview.sqrtP != null ? (
                    <span>
                      √P <strong>{reseedPreview.sqrtP.toFixed(3)}</strong>
                      {reseedPreview.sdProb ? (
                        <span className="manual-intervention-hint">
                          {' '}
                          · ~90% CI {(reseedPreview.sdProb.lo * 100).toFixed(1)}–
                          {(reseedPreview.sdProb.hi * 100).toFixed(1)}%
                        </span>
                      ) : null}
                    </span>
                  ) : (
                    <span>Enter P for CI preview</span>
                  )}
                </>
              ) : (
                <span>Enter p fair (0–1) or x logit</span>
              )}
            </div>
            <div className="manual-ind-card-body manual-ind-card-body--row">
              <label className="manual-ind-check">
                <input
                  type="checkbox"
                  checked={reseedLockBaseline}
                  onChange={(e) => setReseedLockBaseline(e.target.checked)}
                  disabled={isPending}
                />
                Lock baseline to line
              </label>
              <button
                type="button"
                className="manual-btn manual-btn-secondary"
                disabled={isPending}
                onClick={handleLoadReseedFromQuote}
              >
                Load quote
              </button>
              <button
                type="button"
                className="manual-btn manual-btn-primary"
                disabled={isPending}
                onClick={handleReseedPosterior}
              >
                {reseedPosterior.isPending ? 'Reseeding…' : 'Reseed'}
              </button>
            </div>
            <span className="manual-ind-card-hint">
              Hard seed Entries + base KF; clears jump excess; full OB re-open. Edit p or x — they stay linked.
            </span>
          </div>
        </div>

        <div
          className={`manual-ind-card manual-ind-card--alt${altPolicyActive ? ' manual-ind-card--active' : ''}`}
        >
          <div className="manual-ind-card-head">
            <span className="manual-ind-card-title">Alt ladders</span>
            <span
              className={
                altOperatorExplicit || altMarginsStored
                  ? `manual-ind-pill manual-ind-pill--on${
                      altOperatorExplicit && !altAllowChecked ? ' manual-ind-pill--warn' : ''
                    }`
                  : 'manual-ind-pill'
              }
            >
              {!altOperatorExplicit && !altMarginsStored
                ? 'Default'
                : altBadgeOnlyClamps
                  ? 'Δp clamps'
                  : altAllowChecked
                    ? 'Serving'
                    : 'Held back'}
            </span>
          </div>
          <div className="manual-ind-card-body manual-ind-card-body--stack">
            <div className="manual-ind-alt-quote-strip" aria-label="Alt ladder margination from live quote">
              <span title="Δp clamps for non-base ladder rows (matches ladder_quotes when alt lines exist in config). Published payload alt_overs can still be empty under CI-width / alts_enabled / Serve-alts policy — not because the main quote is one-sided. Includes manual alt clamps when serving.">
                Quote alt Δp{' '}
                <strong>
                  {fmtQuoteDp(quote?.alt_margination_floor, 3)} / {fmtQuoteDp(quote?.alt_margination_ceiling, 3)}
                </strong>
              </span>
              <span title="Main-line Δp on the same quote (always defined when the quote is active).">
                · main Δp{' '}
                <strong>
                  {fmtQuoteDp(quote?.margination_floor, 2)} / {fmtQuoteDp(quote?.margination_ceiling, 2)}
                </strong>
              </span>
            </div>
            <label className="manual-ind-check">
              <input
                type="checkbox"
                checked={altAllowChecked}
                onChange={(e) => {
                  const on = e.target.checked;
                  setAltAllowChecked(on);
                  if (!on) {
                    setAltFloorDirty(false);
                    setAltCeilDirty(false);
                  }
                }}
                disabled={isPending}
              />
              Serve alts
            </label>
            <div className="manual-ind-alt-row-compact">
              <label className="manual-ind-alt-inline">
                <span className="manual-ind-alt-lbl">marg. floor</span>
                <input
                  type="text"
                  inputMode="decimal"
                  className="manual-intervention-input manual-ind-alt-input manual-ind-alt-input--narrow"
                  placeholder="—"
                  value={altFloorInput}
                  onChange={(e) => {
                    setAltFloorInput(e.target.value);
                    setAltFloorDirty(true);
                  }}
                  disabled={isPending || !altAllowChecked}
                  aria-label="Alt margination floor"
                  title="Alt margination floor (Δp)"
                />
              </label>
              <label className="manual-ind-alt-inline">
                <span className="manual-ind-alt-lbl">marg. ceiling</span>
                <input
                  type="text"
                  inputMode="decimal"
                  className="manual-intervention-input manual-ind-alt-input manual-ind-alt-input--narrow"
                  placeholder="—"
                  value={altCeilInput}
                  onChange={(e) => {
                    setAltCeilInput(e.target.value);
                    setAltCeilDirty(true);
                  }}
                  disabled={isPending || !altAllowChecked}
                  aria-label="Alt margination ceiling"
                  title="Alt margination ceiling (Δp)"
                />
              </label>
              <span className="manual-ind-alt-btns">
                <button
                  type="button"
                  className="manual-btn manual-btn-secondary manual-ind-alt-apply"
                  disabled={isPending}
                  onClick={handleApplyAltLadders}
                >
                  Apply
                </button>
                <button
                  type="button"
                  className="manual-btn manual-btn-secondary manual-ind-alt-clear"
                  disabled={isPending || !altPolicyActive}
                  onClick={handleClearManualAlt}
                >
                  Clear manual
                </button>
              </span>
            </div>
          </div>
        </div>

        <div
          className={`manual-ind-card manual-ind-card--logit${logitCustomActive ? ' manual-ind-card--active' : ''}`}
        >
          <div className="manual-ind-card-head">
            <span className="manual-ind-card-title">Logit scale</span>
            <span className={logitCustomActive ? 'manual-ind-pill manual-ind-pill--on' : 'manual-ind-pill'}>
              {logitCustomActive ? 'Custom' : 'Auto'}
            </span>
          </div>
          <div className="manual-ind-card-body manual-ind-card-body--stack">
            <div className="manual-ind-logit-row">
              <input
                type="number"
                step="0.05"
                min="0"
                className="manual-intervention-input manual-ind-logit-input"
                placeholder="Scale"
                value={logitInput}
                onChange={(e) => setLogitInput(e.target.value)}
                aria-label="Operator logit margination scale"
              />
              <button
                type="button"
                className="manual-btn manual-btn-secondary manual-ind-logit-apply"
                disabled={isPending || !String(logitInput).trim()}
                onClick={handleLogitCardApply}
              >
                Apply
              </button>
              {logitCustomActive ? (
                <button
                  type="button"
                  className="manual-btn manual-btn-secondary manual-ind-logit-clear"
                  disabled={isPending}
                  onClick={handleLogitCardClear}
                >
                  Clear
                </button>
              ) : null}
            </div>
            {String(logitInput).trim() ? (
              <div className="manual-logit-preview manual-ind-logit-preview">
                {liveLogitPreview ? (
                  <>
                    <span>
                      Preview <strong>{liveLogitPreview.scale.toFixed(3)}</strong>
                    </span>
                    <span>
                      OVER <strong>{(1 / liveLogitPreview.over.impliedProb).toFixed(3)}</strong> (
                      {(liveLogitPreview.over.impliedProb * 100).toFixed(1)}%)
                    </span>
                    {liveLogitPreview.canPriceUnder && liveLogitPreview.under ? (
                      <span>
                        UNDER <strong>{(1 / liveLogitPreview.under.impliedProb).toFixed(3)}</strong> (
                        {(liveLogitPreview.under.impliedProb * 100).toFixed(1)}%)
                      </span>
                    ) : (
                      <span>UNDER —</span>
                    )}
                  </>
                ) : (
                  <span>Preview needs CI inputs</span>
                )}
              </div>
            ) : null}
          </div>
        </div>

        <div
          className={`manual-ind-card manual-ind-card--logit${probMarginCustomActive ? ' manual-ind-card--active' : ''}`}
        >
          <div className="manual-ind-card-head">
            <span className="manual-ind-card-title">Fixed margin (Δp)</span>
            <span className={probMarginCustomActive ? 'manual-ind-pill manual-ind-pill--on' : 'manual-ind-pill'}>
              {probMarginCustomActive ? 'Custom' : 'Auto'}
            </span>
          </div>
          <div className="manual-ind-card-body manual-ind-card-body--stack">
            <div className="manual-ind-logit-row">
              <label className="manual-ind-alt-inline">
                <span className="manual-ind-alt-lbl">over</span>
                <input
                  type="text"
                  inputMode="decimal"
                  className="manual-intervention-input manual-ind-alt-input manual-ind-alt-input--narrow"
                  placeholder="—"
                  value={probMarginOverInput}
                  onChange={(e) => setProbMarginOverInput(e.target.value)}
                  aria-label="Fixed over margin delta p"
                  title="Fixed over margin vs fair (probability points, e.g. 0.06)"
                />
              </label>
              <label className="manual-ind-alt-inline">
                <span className="manual-ind-alt-lbl">under</span>
                <input
                  type="text"
                  inputMode="decimal"
                  className="manual-intervention-input manual-ind-alt-input manual-ind-alt-input--narrow"
                  placeholder="—"
                  value={probMarginUnderInput}
                  onChange={(e) => setProbMarginUnderInput(e.target.value)}
                  aria-label="Fixed under margin delta p"
                  title="Fixed under margin vs fair (probability points, e.g. 0.06)"
                />
              </label>
              <button
                type="button"
                className="manual-btn manual-btn-secondary manual-ind-logit-apply"
                disabled={isPending}
                onClick={handleProbMarginApply}
              >
                Apply
              </button>
              {probMarginCustomActive ? (
                <button
                  type="button"
                  className="manual-btn manual-btn-secondary manual-ind-logit-clear"
                  disabled={isPending}
                  onClick={handleProbMarginClear}
                >
                  Clear
                </button>
              ) : null}
            </div>
            {(String(probMarginOverInput).trim() || String(probMarginUnderInput).trim()) ? (
              <div className="manual-logit-preview manual-ind-logit-preview">
                {liveFixedMarginPreview && (liveFixedMarginPreview.over || liveFixedMarginPreview.under) ? (
                  <>
                    <span>
                      Preview
                      {liveFixedMarginPreview.line != null ? (
                        <>
                          {' '}
                          @ line <strong>{liveFixedMarginPreview.line}</strong>
                        </>
                      ) : null}
                      {' '}
                      <span className="manual-intervention-hint">
                        (Δp {liveFixedMarginPreview.floor.toFixed(2)}–
                        {liveFixedMarginPreview.ceiling.toFixed(2)})
                      </span>
                    </span>
                    {liveFixedMarginPreview.over ? (
                      <span>
                        OVER{' '}
                        {liveFixedMarginPreview.postedOver != null
                        && Math.abs(liveFixedMarginPreview.postedOver - liveFixedMarginPreview.over.decimal) > 0.005 ? (
                          <>
                            <span className="manual-intervention-hint">
                              {liveFixedMarginPreview.postedOver.toFixed(3)}
                            </span>
                            {' → '}
                          </>
                        ) : null}
                        <strong>{liveFixedMarginPreview.over.decimal.toFixed(3)}</strong> (
                        {(liveFixedMarginPreview.over.implied * 100).toFixed(1)}%
                        {Math.abs(liveFixedMarginPreview.over.effDelta - liveFixedMarginPreview.over.requested) > 1e-6
                          ? ` · eff +${(liveFixedMarginPreview.over.effDelta * 100).toFixed(1)}pp`
                          : ''}
                        )
                      </span>
                    ) : String(probMarginOverInput).trim() ? (
                      <span>OVER — (needs fair)</span>
                    ) : null}
                    {liveFixedMarginPreview.canPriceUnder && liveFixedMarginPreview.under ? (
                      <span>
                        UNDER{' '}
                        {liveFixedMarginPreview.postedUnder != null
                        && Math.abs(liveFixedMarginPreview.postedUnder - liveFixedMarginPreview.under.decimal) > 0.005 ? (
                          <>
                            <span className="manual-intervention-hint">
                              {liveFixedMarginPreview.postedUnder.toFixed(3)}
                            </span>
                            {' → '}
                          </>
                        ) : null}
                        <strong>{liveFixedMarginPreview.under.decimal.toFixed(3)}</strong> (
                        {(liveFixedMarginPreview.under.implied * 100).toFixed(1)}%
                        {Math.abs(liveFixedMarginPreview.under.effDelta - liveFixedMarginPreview.under.requested) > 1e-6
                          ? ` · eff +${(liveFixedMarginPreview.under.effDelta * 100).toFixed(1)}pp`
                          : ''}
                        )
                      </span>
                    ) : (
                      <span>UNDER —</span>
                    )}
                  </>
                ) : (
                  <span>Preview needs fair price at this line</span>
                )}
              </div>
            ) : null}
            <span className="manual-ind-card-hint">
              Overrides logit on each leg when set. Clamped to main Δp floor/ceiling vs fair (same as auto margination).
            </span>
          </div>
        </div>
      </div>

      <div className="manual-intervention-section-label">Margination &amp; CI</div>
      <div className="manual-intervention-diag">
        <div className="manual-intervention-diag-row">
          <span>
            <abbr title="Main-line Δp clamp vs fair">Main Δp</abbr>{' '}
            <strong>
              {quote?.margination_floor != null ? Number(quote.margination_floor).toFixed(2) : '—'} /{' '}
              {quote?.margination_ceiling != null ? Number(quote.margination_ceiling).toFixed(2) : '—'}
            </strong>
          </span>
          <span>
            <abbr title="Alt ladder Δp when alt lines are surfaced on the quote; otherwise —">Alt Δp</abbr>{' '}
            <strong>
              {quote?.alt_margination_floor != null ? Number(quote.alt_margination_floor).toFixed(3) : '—'} /{' '}
              {quote?.alt_margination_ceiling != null ? Number(quote.alt_margination_ceiling).toFixed(3) : '—'}
            </strong>
          </span>
          <span>
            Logit scale{' '}
            <strong>{effScale != null ? effScale.toFixed(3) : '—'}</strong>
            {hasManualLogit && <span className="manual-intervention-hint"> custom</span>}
          </span>
          {ciWidthProb != null && (
            <span>
              CI width (delta p){' '}
              <strong>{ciWidthProb.toFixed(3)}</strong>{' '}
              <span className="manual-intervention-hint">
                ({logitCiSpread != null ? `logit ${logitCiSpread.toFixed(3)}` : 'logit —'})
              </span>
            </span>
          )}
        </div>
        <div className="manual-intervention-diag-row secondary">
          <span title="Implied − fair before [floor, ceiling] clamp (logit path), over / under">
            Raw margin Δp{' '}
            <strong>
              o {fmtMargin(probMarginRawOver)} · u {fmtMargin(probMarginRawUnder)}
            </strong>
          </span>
          <span title="After floor/ceiling clamp, before TOB posted floors (structural logit)">
            Structural Δp{' '}
            <strong>
              o {fmtMargin(probMarginStructOver)} · u {fmtMargin(probMarginStructUnder)}
            </strong>
          </span>
          <span
            title={
              tobCapping
                ? `Posted Δp at marginated decimal (includes TOB max ceilings). TOB over=${tobOver != null ? tobOver.toFixed(3) : '—'} under=${tobUnder != null ? tobUnder.toFixed(3) : '—'}`
                : 'Posted Δp at marginated decimal (1/dec − fair); includes TOB max ceilings when active'
            }
          >
            Posted Δp{tobCapping ? ' · TOB' : ''}{' '}
            <strong>
              o {fmtMargin(probMarginEffOver)} · u {fmtMargin(probMarginEffUnder)}
            </strong>
            {tobCapping && (
              <span className="manual-intervention-hint">
                {' '}
                (floor {tobOver != null ? tobOver.toFixed(2) : '—'} /{' '}
                {tobUnder != null ? tobUnder.toFixed(2) : '—'})
              </span>
            )}
          </span>
        </div>
      </div>

      <div className="manual-intervention-section-label">Quote · suspend &amp; shaping</div>

      {hasManualRow ? (
        <div className="manual-intervention-body">
          <div className="manual-intervention-summary">
            {override?.gate_reason != null && (
              <div className="manual-summary-line">
                <span className="manual-intervention-label">Reason</span>
                <span>{override.gate_reason}</span>
              </div>
            )}
            {hasForceTwoSided && (
              <div className="manual-summary-line">
                <span className="manual-intervention-label">Quote</span>
                <span>Two-sided (under from model when not manual)</span>
              </div>
            )}
            {hasForceOneSided && (
              <div className="manual-summary-line">
                <span className="manual-intervention-label">Quote</span>
                <span>One-sided (no under)</span>
              </div>
            )}
            {hasManualLogit && hasManualRow && (
              <div className="manual-summary-line">
                <span className="manual-intervention-label">Logit scale</span>
                <span>{Number(override.manual_logit_margin_scale).toFixed(3)}</span>
              </div>
            )}
            {(hasManualProbMarginOver || hasManualProbMarginUnder) && (
              <div className="manual-summary-line">
                <span className="manual-intervention-label">Fixed margin (Δp)</span>
                <span>
                  over {hasManualProbMarginOver ? Number(override.manual_prob_margin_over).toFixed(3) : '—'}
                  {' · '}
                  under {hasManualProbMarginUnder ? Number(override.manual_prob_margin_under).toFixed(3) : '—'}
                </span>
              </div>
            )}
            {(override?.manual_price != null && override.manual_price > 0) && (
              <div className="manual-summary-line">
                <span className="manual-intervention-label">Manual over</span>
                <span>
                  {override.manual_price.toFixed(2)}
                  {override?.manual_line != null ? ` @ ${override.manual_line}` : ''}
                </span>
              </div>
            )}
            {(override?.manual_price_under != null && override.manual_price_under > 0) && (
              <div className="manual-summary-line">
                <span className="manual-intervention-label">Manual under</span>
                <span>{override.manual_price_under.toFixed(2)}</span>
              </div>
            )}
            {override?.gated_by && (
              <div className="manual-summary-line">
                <span className="manual-intervention-label">By</span>
                <span>{override.gated_by}</span>
              </div>
            )}
            {override?.gated_at && (
              <div className="manual-summary-line">
                <span className="manual-intervention-label">At</span>
                <span>{new Date(override.gated_at).toLocaleString()}</span>
              </div>
            )}
          </div>
          <div className="manual-gate-action-stack">
            <div className="manual-gate-shape-row">
              <button
                type="button"
                className="manual-btn manual-btn-primary"
                title="Apply two-sided quote shaping and refresh OB depth (two-sided ladder)"
                onClick={handleTwoSided}
                disabled={isPending}
              >
                Two-sided
              </button>
              <button
                type="button"
                className="manual-btn manual-btn-primary"
                title="Apply one-sided quote shaping and refresh OB as over-only"
                onClick={handleOneSided}
                disabled={isPending}
              >
                One-sided
              </button>
            </div>
            <button
              type="button"
              className="manual-btn manual-btn-danger manual-gate-remove-row"
              onClick={handleUngate}
              disabled={isPending}
            >
              {isPending ? 'Removing…' : 'Remove override'}
            </button>
          </div>
        </div>
      ) : (
        <div className="manual-intervention-actions">
          <button
            type="button"
            className="manual-btn manual-btn-danger"
            onClick={handleSuspend}
            disabled={isPending}
          >
            Suspend quoting
          </button>
          <button
            type="button"
            className="manual-btn manual-btn-primary"
            onClick={handleTwoSided}
            disabled={isPending}
          >
            Two-sided + OB refresh
          </button>
          <button
            type="button"
            className="manual-btn manual-btn-primary"
            onClick={handleOneSided}
            disabled={isPending}
          >
            One-sided + OB refresh
          </button>
          <p className="manual-intervention-footnote">
            Suspend blocks quotes. Shaping and operator logit changes can re-seed OB on the writer.
          </p>
        </div>
      )}

      {(gateMarket.error ||
        ungateMarket.error ||
        setMuteCompetitor.error ||
        setManualBaselineLock.error ||
        clearManualBaselineLock.error ||
        injectUncertainty.error ||
        reseedPosterior.error) && (
        <div className="manual-intervention-error">
          {(gateMarket.error?.response?.data?.detail ||
            ungateMarket.error?.response?.data?.detail ||
            setMuteCompetitor.error?.response?.data?.detail ||
            setManualBaselineLock.error?.response?.data?.detail ||
            clearManualBaselineLock.error?.response?.data?.detail ||
            (typeof injectUncertainty.error?.response?.data?.detail === 'object'
              ? injectUncertainty.error?.response?.data?.detail?.message
              : injectUncertainty.error?.response?.data?.detail) ||
            (typeof reseedPosterior.error?.response?.data?.detail === 'object'
              ? reseedPosterior.error?.response?.data?.detail?.message
              : reseedPosterior.error?.response?.data?.detail)) ||
            gateMarket.error?.message ||
            ungateMarket.error?.message ||
            setMuteCompetitor.error?.message ||
            setManualBaselineLock.error?.message ||
            clearManualBaselineLock.error?.message ||
            injectUncertainty.error?.message ||
            reseedPosterior.error?.message ||
            'Request failed'}
        </div>
      )}
      <ModalLayer />
    </div>
  );
}

export default ManualGatingPanel;
